import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../stores/auth';
import { customerProductsApi, customerInvoicesApi, customerTicketsApi } from '../lib/api';
import { Box, Globe, Server, FileText, MessageSquare, CreditCard, CalendarClock, Plus } from 'lucide-react';
import clsx from 'clsx';
import { useCurrency } from '../hooks/useCurrency';
import { useTranslation } from 'react-i18next';

interface Renewal {
  id: string;
  type: string;
  domain?: string;
  status: string;
  packageName?: string | null;
  billingCycle: string;
  price: number;
  nextDueDate: string;
}

interface ServicesSummary {
  total: number;
  active: number;
  suspended: number;
  pending: number;
  servers: number;
  upcoming: Renewal[];
}

interface Invoice {
  id: string;
  number: string;
  status: string;
  total: number;
  paidAmount: number;
  currency: string;
  dueDate: string;
}

interface Ticket {
  id: string;
  subject: string;
  status: string;
  createdAt: string;
}

function formatCurrency(amount: number, currency: string) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount / 100);
}

const cycleAbbreviations: Record<string, string> = {
  MONTHLY: 'mo',
  QUARTERLY: 'qtr',
  ANNUALLY: 'yr',
  BIENNIALLY: '2yr',
};

/** Whole days from today, negative once the date is behind us. */
function daysUntil(date: string): number {
  const msPerDay = 24 * 60 * 60 * 1000;
  const then = new Date(date);
  then.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((then.getTime() - today.getTime()) / msPerDay);
}

function renewalTone(days: number): string {
  if (days < 0) return 'text-red-600';
  if (days <= 7) return 'text-amber-600';
  return 'text-gray-500';
}

/** Takes `t` rather than calling it: this sits outside the component. */
function renewalLabel(days: number, t: (key: string, vars?: Record<string, unknown>) => string): string {
  if (days < 0) return t('dashboard.overdueDays', { count: Math.abs(days) });
  if (days === 0) return t('dashboard.renewsToday');
  if (days === 1) return t('dashboard.renewsTomorrow');
  return t('dashboard.renewsInDays', { count: days });
}

export default function DashboardPage() {
  const { t } = useTranslation();
  const { customer } = useAuthStore();
  const { formatPrice } = useCurrency();

  const { data, isLoading } = useQuery({
    queryKey: ['customer-services-summary'],
    queryFn: () => customerProductsApi.summary(),
  });

  const { data: invoicesData } = useQuery({
    queryKey: ['customer-invoices-pending'],
    queryFn: () => customerInvoicesApi.list({ status: 'PENDING', limit: 3 }),
  });

  const { data: ticketsData } = useQuery({
    queryKey: ['customer-tickets-open'],
    queryFn: () => customerTicketsApi.list({ status: 'OPEN', limit: 5 }),
  });

  const summary = data?.data as ServicesSummary | undefined;
  const renewals = summary?.upcoming ?? [];
  const unpaidInvoices = (invoicesData?.data as Invoice[]) || [];
  const openTickets = (ticketsData?.data as Ticket[]) || [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Welcome back, {customer?.firstName || 'there'}!
        </h1>
        <p className="text-gray-500">{t('dashboard.overview')}</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-brand-100 rounded-lg">
              <Box className="h-6 w-6 text-brand-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('dashboard.totalProducts')}</p>
              <p className="text-2xl font-bold">{summary?.total ?? '—'}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <Globe className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('dashboard.activeServices')}</p>
              <p className="text-2xl font-bold">{summary?.active ?? '—'}</p>
              {!!summary?.suspended && (
                <p className="text-xs text-red-600">{summary.suspended} suspended</p>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Server className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('dashboard.servers')}</p>
              <p className="text-2xl font-bold">{summary?.servers ?? '—'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions & Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Unpaid Invoices */}
        <div className="bg-white rounded-xl shadow-sm">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-gray-400" />
              <h2 className="font-semibold">{t('dashboard.unpaidInvoices')}</h2>
            </div>
            <Link to="/invoices" className="text-sm text-brand-600 hover:text-brand-800">
              {t('dashboard.viewAll')}
            </Link>
          </div>
          {unpaidInvoices.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              <p>{t('dashboard.noUnpaid')}</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {unpaidInvoices.map((invoice) => {
                const amountDue = invoice.total - invoice.paidAmount;
                return (
                  <div key={invoice.id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                    <div>
                      <p className="font-medium">{invoice.number}</p>
                      <p className="text-sm text-gray-500">
                        Due {new Date(invoice.dueDate).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-medium">{formatCurrency(amountDue, invoice.currency)}</span>
                      <Link
                        to={`/invoices/${invoice.id}?pay=true`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700"
                      >
                        <CreditCard className="h-4 w-4" />
                        Pay
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Open Tickets */}
        <div className="bg-white rounded-xl shadow-sm">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-gray-400" />
              <h2 className="font-semibold">{t('dashboard.openTickets')}</h2>
              {openTickets.length > 0 && (
                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                  {openTickets.length}
                </span>
              )}
            </div>
            <Link to="/tickets" className="text-sm text-brand-600 hover:text-brand-800">
              {t('dashboard.viewAll')}
            </Link>
          </div>
          {openTickets.length === 0 ? (
            <div className="p-6 text-center">
              <p className="text-gray-500 mb-3">{t('dashboard.noOpenTickets')}</p>
              <Link
                to="/tickets"
                className="inline-flex items-center gap-2 px-4 py-2 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700"
              >
                <Plus className="h-4 w-4" />
                {t('dashboard.newTicket')}
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {openTickets.slice(0, 3).map((ticket) => (
                <Link
                  key={ticket.id}
                  to={`/tickets/${ticket.id}`}
                  className="p-4 block hover:bg-gray-50"
                >
                  <p className="font-medium truncate">{ticket.subject}</p>
                  <p className="text-sm text-gray-500">
                    Opened {new Date(ticket.createdAt).toLocaleDateString()}
                  </p>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Upcoming renewals. The full list of services lives on /products; what
          belongs here is only what is about to need attention. */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-gray-400" />
            <h2 className="font-semibold">{t('dashboard.upcomingRenewals')}</h2>
          </div>
          <Link to="/products" className="text-sm text-brand-600 hover:text-brand-800">
            {t('dashboard.allProducts')}
          </Link>
        </div>

        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin h-8 w-8 border-4 border-brand-500 border-t-transparent rounded-full mx-auto" />
            <p className="mt-2 text-gray-500">{t('dashboard.loadingRenewals')}</p>
          </div>
        ) : renewals.length === 0 ? (
          <div className="p-8 text-center">
            <CalendarClock className="h-12 w-12 mx-auto text-gray-400" />
            <p className="mt-4 text-gray-500">{t('dashboard.nothingUpcoming')}</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {renewals.map((renewal) => {
              const days = daysUntil(renewal.nextDueDate);
              return (
                <div key={renewal.id} className="p-4 flex items-center justify-between gap-4 hover:bg-gray-50">
                  <div className="min-w-0">
                    <p className="font-medium truncate">{renewal.domain || renewal.type}</p>
                    <p className="text-sm text-gray-500 truncate">
                      {renewal.packageName || renewal.type}
                      {renewal.status === 'SUSPENDED' && (
                        <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                          {t('dashboard.suspended')}
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-medium">
                      {formatPrice(renewal.price)}
                      <span className="text-gray-400 font-normal">
                        /{cycleAbbreviations[renewal.billingCycle] || renewal.billingCycle.toLowerCase()}
                      </span>
                    </p>
                    <p className={clsx('text-sm', renewalTone(days))}>
                      {renewalLabel(days, t)} · {new Date(renewal.nextDueDate).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
